import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Monitor,
  Folder,
  Activity,
  Building2,
  RefreshCw,
  LogOut,
  Menu,
  X,
  LucideIcon,
} from 'lucide-react';

export type DashboardSection = 'overview' | 'locks' | 'groups' | 'events' | 'companies';

const NAV: Array<{ id: DashboardSection; label: string; icon: LucideIcon }> = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'locks', label: 'Locks', icon: Monitor },
  { id: 'groups', label: 'Groups', icon: Folder },
  { id: 'events', label: 'Events', icon: Activity },
  { id: 'companies', label: 'Companies', icon: Building2 },
];

interface DashboardLayoutProps {
  active: DashboardSection;
  onSelect: (s: DashboardSection) => void;
  userName: string;
  userEmail?: string;
  onRefresh: () => void;
  refreshing: boolean;
  onLogout: () => void;
  badges?: Partial<Record<DashboardSection, number>>;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  active,
  onSelect,
  userName,
  userEmail,
  onRefresh,
  refreshing,
  onLogout,
  badges,
  title,
  subtitle,
  actions,
  children,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawerOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleSelect = (s: DashboardSection) => {
    onSelect(s);
    setDrawerOpen(false);
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800/60 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg overflow-hidden shadow-lg shadow-indigo-500/30 flex-shrink-0">
          <img src="/icon.png" alt="Lockwaste" className="w-full h-full object-cover" />
        </div>
        <span className="text-white font-bold tracking-tight">LOCKWASTE</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Sections">
        {NAV.map(({ id, label, icon: Icon }) => {
          const isActive = active === id;
          const badge = badges?.[id];
          return (
            <button
              key={id}
              onClick={() => handleSelect(id)}
              aria-current={isActive ? 'page' : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                isActive
                  ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-900/40'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {badge ? (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                  isActive ? 'bg-white/20 text-white' : 'bg-indigo-600 text-white'
                }`}>
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {/* Footer: user + actions */}
      <div className="border-t border-slate-800/60 p-3 flex-shrink-0">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-sm font-bold flex-shrink-0">
            {(userName || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">{userName || 'User'}</p>
            {userEmail && <p className="text-xs text-slate-400 truncate">{userEmail}</p>}
          </div>
        </div>
        <div className="mt-2 flex gap-2">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={onLogout}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-300 hover:text-white bg-slate-800/60 hover:bg-red-600/80 rounded-lg transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-slate-900 z-30">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-64 bg-slate-900 animate-slide-up">{SidebarInner}</aside>
        </div>
      )}

      {/* Content */}
      <div className="lg:pl-64 flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 h-16">
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-extrabold text-slate-900 tracking-tight truncate">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
            </div>
            {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-7xl mx-auto">{children}</main>
      </div>

      {/* Mobile close hint when drawer open handled by overlay; ensure X reachable */}
      {drawerOpen && (
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden fixed top-3 right-3 z-50 p-2 bg-white rounded-lg shadow text-slate-600"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default DashboardLayout;
